import { StateCreator } from 'zustand'
import { PortfolioItem, PortfolioCategory } from '@types'

export interface PortfolioSlice {
  portfolio: {
    items: PortfolioItem[]
    isLoading: boolean
    error: string | null
    selectedCategory: PortfolioCategory | 'all'
    selectedItem: PortfolioItem | null
    fetchPortfolio: () => Promise<void>
    setSelectedCategory: (category: PortfolioCategory | 'all') => void
    setSelectedItem: (item: PortfolioItem | null) => void
    getPortfolioByCategory: (category: PortfolioCategory | 'all') => PortfolioItem[]
  }
}

const mockPortfolio: PortfolioItem[] = [
  {
    id: '1',
    title: 'Digital Twin Industrial',
    description: 'Gemelo digital completo de una planta manufacturera que permite monitoreo en tiempo real y optimización de procesos.',
    image: '/images/portfolio/digital-twin-industrial.jpg',
    category: 'industrial',
    technologies: ['Unity 3D', 'IoT', 'Real-time Data'],
    client: 'Industria Manufacturera S.A.',
    year: 2024,
    features: [
      'Monitoreo en tiempo real',
      'Análisis predictivo',
      'Optimización de procesos',
      'Visualización 3D',
    ],
    gallery: [
      '/images/portfolio/digital-twin-1.jpg',
      '/images/portfolio/digital-twin-2.jpg',
      '/images/portfolio/digital-twin-3.jpg',
    ],
  },
  {
    id: '2',
    title: 'Simulador de Fútbol',
    description: 'Aplicación de realidad virtual de fútbol para entrenamiento de jugadores de fútbol.',
    image: '/images/portfolio/metaverso-corporativo.jpg',
    category: 'gaming',
    technologies: ['Unity', 'Meta Quest 3', 'C#', 'Firebase'],
    client: 'Hisense',
    year: 2025,
    features: [
      'Hand Tracking',
      'Eventos corporativos',
      'Activación de marca',
      'Interacción con el usuario',
    ],
    gallery: [
      '/images/portfolio/metaverso-1.jpg',
      '/images/portfolio/metaverso-2.jpg',
      '/images/portfolio/metaverso-3.jpg',
    ],
  },
  {
    id: '3',
    title: 'Universidad Virtual',
    description: 'Campus universitario virtual con laboratorios interactivos, aulas inmersivas y experiencias educativas en 3D.',
    image: '/images/portfolio/universidad-virtual.jpg',
    category: 'educacion',
    technologies: ['Unreal Engine', 'VR', 'Educational Tech'],
    client: 'Universidad Virtual Colombia',
    year: 2023,
    features: [
      'Laboratorios virtuales',
      'Aulas inmersivas',
      'Simulaciones educativas',
      'Colaboración en tiempo real',
    ],
    gallery: [
      '/images/portfolio/universidad-1.jpg',
      '/images/portfolio/universidad-2.jpg',
      '/images/portfolio/universidad-3.jpg',
    ],
  }
  //,
  //{
  //  id: '4',
  //  title: 'Simulador Médico',
  //  description: 'Entorno de entrenamiento médico con Meta Humans para practicar procedimientos quirúrgicos de forma segura.',
  //  image: '/images/portfolio/simulador-medico.jpg',
  //  category: 'medicina',
  //  technologies: ['Unreal Engine', 'AI', 'Medical Simulation'],
  //  client: 'Hospital Universitario',
  //  year: 2023,
  //  features: [
  //    'Procedimientos quirúrgicos',
  //    'Meta Humans realistas',
  //    'Feedback en tiempo real',
  //    'Evaluación de habilidades',
  //  ],
  //  gallery: [
  //    '/images/portfolio/medico-1.jpg',
  //    '/images/portfolio/medico-2.jpg',
  //    '/images/portfolio/medico-3.jpg',
  //  ],
  //},
  //{
  //  id: '5',
  //  title: 'Showroom Virtual',
  //  description: 'Tienda virtual inmersiva que permite a los clientes explorar y probar productos antes de comprarlos.',
  //  image: '/images/portfolio/showroom-virtual.jpg',
  //  category: 'e-commerce',
  //  technologies: ['WebXR', 'AR', 'E-commerce'],
  //  client: 'Fashion Retail Co.',
  //  year: 2024,
  //  features: [
  //    'Exploración de productos',
  //    'Prueba virtual',
  //    'Integración de pagos',
  //    'Personalización',
  //  ],
  //  gallery: [
  //    '/images/portfolio/showroom-1.jpg',
  //    '/images/portfolio/showroom-2.jpg',
  //    '/images/portfolio/showroom-3.jpg',
  //  ],
  //},
  //{
  //  id: '6',
  //  title: 'Juego VR Multijugador',
  //  description: 'Experiencia de juego colaborativa en realidad virtual con mecánicas innovadoras y gráficos de última generación.',
  //  image: '/images/portfolio/juego-vr.jpg',
  //  category: 'gaming',
  //  technologies: ['Unity 3D', 'VR', 'Multiplayer'],
  //  client: 'Gaming Studio',
  //  year: 2023,
  //  features: [
  //    'Multijugador cooperativo',
  //    'Mecánicas innovadoras',
  //    'Gráficos de alta calidad',
  //    'Experiencia inmersiva',
  //  ],
  //  gallery: [
  //    '/images/portfolio/gaming-1.jpg',
  //    '/images/portfolio/gaming-2.jpg',
  //    '/images/portfolio/gaming-3.jpg',
  //  ],
  //},
]

export const createPortfolioSlice: StateCreator<PortfolioSlice> = (set, get) => ({
  portfolio: {
    items: mockPortfolio,
    isLoading: false,
    error: null,
    selectedCategory: 'all',
    selectedItem: null,

    fetchPortfolio: async () => {
      set((state) => ({
        portfolio: {
          ...state.portfolio,
          isLoading: true,
        },
      }))

      try {
        // Simular llamada a API
        await new Promise((resolve) => setTimeout(resolve, 1000))

        set((state) => ({
          portfolio: {
            ...state.portfolio,
            items: mockPortfolio,
            isLoading: false,
          },
        }))
      } catch (error) {
        set((state) => ({
          portfolio: {
            ...state.portfolio,
            isLoading: false,
            error: 'Error al cargar el portafolio',
          },
        }))
      }
    },

    setSelectedCategory: (category: PortfolioCategory | 'all') => {
      set((state) => ({
        portfolio: {
          ...state.portfolio,
          selectedCategory: category,
        },
      }))
    },

    setSelectedItem: (item: PortfolioItem | null) => {
      set((state) => ({
        portfolio: {
          ...state.portfolio,
          selectedItem: item,
        },
      }))
    },

    getPortfolioByCategory: (category: PortfolioCategory | 'all') => {
      const { items } = get().portfolio
      if (category === 'all') return items
      return items.filter((item) => item.category === category)
    },
  },
}) 